import { useState, useEffect } from "react";
import { useResetListener } from "../../../hooks/useResetListener";
import SectionHeader from "../../ui/SectionHeader";
import { FiAlertTriangle, FiEdit3 } from "react-icons/fi";

export default function SignatureSection({
  onChange,
  initialData,
  disabled = false,
  textVariant = "trademark",
  hideCapacity = false,
  hideNotice = false,
}: any) {
  const [accepted, setAccepted] = useState(false);
  const [openSelect, setOpenSelect] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [capacity, setCapacity] = useState("");
  const isDesignText = textVariant === "design";
  const effectiveAccepted = hideNotice || accepted;

  useEffect(() => {
    if (!initialData) return;

    const emptyState =
      !accepted &&
      firstName === "" &&
      lastName === "" &&
      (hideCapacity || capacity === "");

    if (!emptyState) return;

    setAccepted(initialData.accepted || false);

    const parts = (initialData.signerName || "").trim().split(" ");
    setFirstName(parts[0] || "");
    setLastName(parts.slice(1).join(" ") || "");
    setCapacity(initialData.signerCapacity || "");
  }, [initialData, accepted, firstName, lastName, capacity, hideCapacity]);

  useResetListener(() => {
    setAccepted(false);
    setFirstName("");
    setLastName("");
    setCapacity("");
    setOpenSelect(false);

    onChange?.(null);

    window.dispatchEvent(
      new CustomEvent("sectionComplete", {
        detail: { section: "signature", complete: false },
      }),
    );
  });

  useEffect(() => {
    const complete =
      effectiveAccepted &&
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      (hideCapacity || capacity.trim().length > 0);

    window.dispatchEvent(
      new CustomEvent("sectionComplete", {
        detail: { section: "signature", complete },
      }),
    );

    if (complete) {
      onChange?.({
        signerName: `${firstName.trim()} ${lastName.trim()}`,
        signerCapacity: hideCapacity ? null : capacity.trim(),
        accepted: true,
      });
    } else {
      onChange?.(null);
    }
  }, [effectiveAccepted, firstName, lastName, capacity, hideCapacity, onChange]);

  return (
    <section className="space-y-4 border-t border-gray-300 pt-6">
      <SectionHeader icon={<FiEdit3 size={20} />} title="Signature" />

      {!hideNotice && (
        <div
          className={`rounded-3xl border p-5 shadow-sm ring-1 md:p-6 text-sm ${
            accepted
              ? "border-green-400 bg-green-50 ring-green-200 text-gray-700"
              : "border-yellow-300 bg-yellow-50 ring-yellow-200 text-gray-700"
          }`}
        >
          <div className="mb-4 flex items-start gap-3">
            <span
              className={`mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-2xl shadow-inner ${
                accepted
                  ? "bg-green-200 text-green-900"
                  : "bg-yellow-200 text-yellow-900"
              }`}
            >
              <FiAlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <p className="text-base font-semibold text-gray-900">
                Important notice
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Please read this section carefully before continuing.
              </p>
            </div>
          </div>

          {isDesignText ? (
            <>
              <p className="mb-3 leading-6">
                Please review your application carefully, because once it has been
                submitted:
              </p>
              <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-gray-700">
                <li>
                  you cannot change the representation of the design after filing
                  (you cannot delete, replace or add views unless requested by the
                  EUIPO examiner)
                </li>
                <li>
                  fees will not be refunded after a refusal due to an unresolved
                  deficiency, unless no filing date has been granted.
                </li>
              </ul>
              <p className="mt-3 leading-6 text-gray-700">
                Fees will not be refunded after withdrawal, unless the withdrawal
                is requested on the same day on which the application was filed,
                or no filing date has been granted.
              </p>
            </>
          ) : (
            <>
              <p className="mb-3 leading-6 text-gray-700">
                Please review your application carefully. The following is only
                possible if requested on the same day:
              </p>
              <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-gray-700">
                <li>Modification of your mark</li>
                <li>Extension of goods and services list</li>
                <li>Refund of fees after withdrawal or rejection</li>
              </ul>
            </>
          )}

          <label
            className={`mt-5 flex items-center gap-2 text-sm ${disabled ? "opacity-50" : ""}`}
          >
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => {
                if (!disabled) {
                  setAccepted(e.target.checked);
                }
              }}
              disabled={disabled}
              className="h-4 w-4 rounded border-gray-300 text-blue-800 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-800">
              I understand and accept
            </span>
          </label>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-4 ${hideCapacity ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
        <div>
          <label className="block text-sm font-medium mb-1">First name*</label>
          <input
            type="text"
            disabled={!effectiveAccepted || disabled}
            value={firstName}
            onChange={(e) => {
              if (!disabled) {
                setFirstName(e.target.value);
              }
            }}
            className={`w-full border rounded-md p-2 ${
              effectiveAccepted && !disabled
                ? "border-gray-300 focus:ring-1 focus:ring-blue-400"
                : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Last name*</label>
          <input
            type="text"
            disabled={!effectiveAccepted || disabled}
            value={lastName}
            onChange={(e) => {
              if (!disabled) {
                setLastName(e.target.value);
              }
            }}
            className={`w-full border rounded-md p-2 ${
              effectiveAccepted && !disabled
                ? "border-gray-300 focus:ring-1 focus:ring-blue-400"
                : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          />
        </div>

        {!hideCapacity && (
          <div className="relative">
            <label className="block text-sm font-medium mb-1">
              Signer's capacity*
            </label>
            <select
              disabled={!effectiveAccepted || disabled}
              value={capacity}
              onChange={(e) => {
                if (!disabled) {
                  setCapacity(e.target.value);
                }
              }}
              onFocus={() => setOpenSelect(true)}
              onBlur={() => setOpenSelect(false)}
              className={`w-full border rounded-md p-2 appearance-none ${
                effectiveAccepted && !disabled
                  ? "border-gray-300 focus:ring-1 focus:ring-blue-400"
                  : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              <option value="">Select</option>
              <option value="APPLICANT">Applicant</option>
              <option value="EMPLOYEE_REPRESENTATIVE">
                Employee representative
              </option>
              <option value="LEGAL_PRACTITIONER">Legal practitioner</option>
              <option value="PROFESSIONAL_PRACTITIONER">
                Qualified representative
              </option>
            </select>

            <span className="absolute right-3 top-9 text-gray-500 text-xs pointer-events-none">
              {openSelect ? "^" : "˅"}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
